<script setup lang="ts">
  import { computed } from 'vue';
  import PosUiIcon from './PosUiIcon.vue';

  type TColor =
    | 'primary'
    | 'neutral'
    | 'success'
    | 'error'
    | 'warning'
    | 'info';
  type TVariant = 'solid' | 'soft' | 'outline' | 'ghost';
  type TSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

  const props = withDefaults(
    defineProps<{
      color?: TColor;
      variant?: TVariant;
      size?: TSize;
      icon?: string;
      loading?: boolean;
      disabled?: boolean;
      block?: boolean;
      type?: 'button' | 'submit';
    }>(),
    {
      color: 'primary',
      variant: 'solid',
      size: 'md',
      icon: undefined,
      loading: false,
      disabled: false,
      block: false,
      type: 'button',
    }
  );

  const emit = defineEmits<{
    (e: 'click', ev: MouseEvent): void;
  }>();

  const classes = computed(() => [
    `color-${props.color}`,
    `variant-${props.variant}`,
    `size-${props.size}`,
    { block: props.block, 'is-loading': props.loading },
  ]);

  function onClick(ev: MouseEvent) {
    if (props.disabled || props.loading) return;
    emit('click', ev);
  }
</script>

<template>
  <button
    class="pos-ui-button"
    :class="classes"
    :type="type"
    :disabled="disabled || loading"
    @click="onClick"
  >
    <span v-if="loading" class="pos-ui-button-spinner" />
    <PosUiIcon v-else-if="icon" :name="icon" class="pos-ui-button-icon" />
    <span v-if="$slots.default" class="pos-ui-button-label"><slot /></span>
  </button>
</template>

<style scoped lang="scss">
  .pos-ui-button {
    /* border-0 + solid: Vuetify's reset sets button { border-style: none },
       which silently kills the outline variants' tw-border (width-only). */
    @apply tw-inline-flex tw-select-none tw-items-center tw-justify-center tw-whitespace-nowrap tw-rounded-lg tw-border-0 tw-border-solid tw-font-medium tw-transition-colors tw-duration-150;

    &:focus-visible {
      @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40 tw-ring-offset-1;
    }

    &.block {
      @apply tw-flex tw-w-full;
    }

    // Margin, not flex gap: old POS hardware (Chrome < 84). The doubled
    // class outranks the label overrides of icon-only-on-phones consumers
    // (tw-not-sr-only resets margins); a hidden label keeps the icon centred.
    &.pos-ui-button .pos-ui-button-spinner + .pos-ui-button-label,
    &.pos-ui-button .pos-ui-button-icon + .pos-ui-button-label {
      @apply tw-ml-1.5;
    }

    // Sizes
    &.size-xs {
      @apply tw-h-8 tw-px-2.5 tw-text-xs;
      .pos-ui-button-icon {
        @apply tw-text-base;
      }
    }
    &.size-sm {
      @apply tw-h-9 tw-px-3 tw-text-sm;
      .pos-ui-button-icon {
        @apply tw-text-lg;
      }
    }
    &.size-md {
      @apply tw-h-11 tw-px-4 tw-text-sm;
      .pos-ui-button-icon {
        @apply tw-text-xl;
      }
    }
    &.size-lg {
      @apply tw-h-12 tw-px-5 tw-text-base;
      .pos-ui-button-icon {
        @apply tw-text-xl;
      }
    }
    &.size-xl {
      @apply tw-h-14 tw-px-6 tw-text-base;
      .pos-ui-button-icon {
        @apply tw-text-2xl;
      }
    }

    // Primary (orange — reserved for each screen's main action)
    &.color-primary {
      &.variant-solid {
        @apply tw-bg-orange-600 tw-text-on-color hover:tw-bg-orange-700 active:tw-bg-orange-800;
      }
      &.variant-soft {
        @apply tw-bg-orange-50 tw-text-orange-700 hover:tw-bg-orange-100;
      }
      &.variant-outline {
        @apply tw-border tw-border-orange-600 tw-text-orange-700 hover:tw-bg-orange-50;
      }
      &.variant-ghost {
        @apply tw-text-orange-700 hover:tw-bg-orange-50;
      }
    }

    // Neutral (gray)
    &.color-neutral {
      &.variant-solid {
        @apply tw-bg-neutral-800 tw-text-on-inverse hover:tw-bg-neutral-700 active:tw-bg-neutral-900;
      }
      &.variant-soft {
        @apply tw-bg-neutral-100 tw-text-neutral-700 hover:tw-bg-neutral-200;
      }
      &.variant-outline {
        @apply tw-border tw-border-neutral-300 tw-bg-surface tw-text-neutral-700 hover:tw-bg-neutral-50;
      }
      &.variant-ghost {
        @apply tw-text-neutral-600 hover:tw-bg-neutral-100;
      }
    }

    // Success (emerald — money / done)
    &.color-success {
      &.variant-solid {
        @apply tw-bg-emerald-600 tw-text-on-color hover:tw-bg-emerald-700 active:tw-bg-emerald-800;
      }
      &.variant-soft {
        @apply tw-bg-emerald-50 tw-text-emerald-700 hover:tw-bg-emerald-100;
      }
      &.variant-outline {
        @apply tw-border tw-border-emerald-600 tw-text-emerald-700 hover:tw-bg-emerald-50;
      }
      &.variant-ghost {
        @apply tw-text-emerald-700 hover:tw-bg-emerald-50;
      }
    }

    // Error (red)
    &.color-error {
      &.variant-solid {
        @apply tw-bg-red-600 tw-text-on-color hover:tw-bg-red-700 active:tw-bg-red-800;
      }
      &.variant-soft {
        @apply tw-bg-red-50 tw-text-red-700 hover:tw-bg-red-100;
      }
      &.variant-outline {
        @apply tw-border tw-border-red-600 tw-text-red-600 hover:tw-bg-red-50;
      }
      &.variant-ghost {
        @apply tw-text-red-600 hover:tw-bg-red-50;
      }
    }

    // Warning (amber)
    &.color-warning {
      &.variant-solid {
        @apply tw-bg-amber-500 tw-text-on-color hover:tw-bg-amber-600 active:tw-bg-amber-700;
      }
      &.variant-soft {
        @apply tw-bg-amber-50 tw-text-amber-700 hover:tw-bg-amber-100;
      }
      &.variant-outline {
        @apply tw-border tw-border-amber-500 tw-text-amber-700 hover:tw-bg-amber-50;
      }
      &.variant-ghost {
        @apply tw-text-amber-700 hover:tw-bg-amber-50;
      }
    }

    // Info (sky)
    &.color-info {
      &.variant-solid {
        @apply tw-bg-sky-600 tw-text-on-color hover:tw-bg-sky-700 active:tw-bg-sky-800;
      }
      &.variant-soft {
        @apply tw-bg-sky-50 tw-text-sky-700 hover:tw-bg-sky-100;
      }
      &.variant-outline {
        @apply tw-border tw-border-sky-500 tw-text-sky-700 hover:tw-bg-sky-50;
      }
      &.variant-ghost {
        @apply tw-text-sky-700 hover:tw-bg-sky-50;
      }
    }

    // Disabled: declared after the color rules so it wins the specificity
    // tie. Loading keeps the color (it's a busy state, not unavailable).
    &:disabled {
      @apply tw-cursor-not-allowed;

      &:not(.is-loading) {
        @apply tw-border-transparent tw-bg-neutral-100 tw-text-neutral-400;

        // Variant hover rules compile to (0,4,0) and would beat the plain
        // disabled override; restate the skin at :hover so it ties and wins
        // by source order.
        &:hover {
          @apply tw-border-transparent tw-bg-neutral-100 tw-text-neutral-400;
        }
      }

      &.is-loading {
        @apply tw-opacity-60;
      }
    }
  }

  .pos-ui-button-spinner {
    @apply tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-2 tw-border-current tw-border-t-transparent;
  }

  .pos-ui-button-label {
    @apply tw-inline-flex tw-items-center;
  }
</style>
